/**
 * INTEGRATION MULTI-REQUIREMENT COMPONENT
 *
 * Like IntegrationRequirement but allows selecting a list of integrations.
 * Each selected integration is shown as a row; an "Add" button lets the user
 * open the selector flyout again to add more.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { X, Plug, Plus } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';
import { IntegrationBaseSelector } from '@/features/Integrations/components/IntegrationBaseSelector';
import type { IntegrationBase } from '@/features/Integrations/types/integration';
import { getIntegrationDescription } from '@/shared/utils/i18nHelper';

interface IntegrationFilter {
  type: 'classification' | 'name';
  value: string;
}

interface InstallerField {
  name: string;
  type: string;
  label?: string;
  description?: string;
  filters?: IntegrationFilter;
  required?: boolean;
  maxCount?: number;
}

interface IntegrationMultiRequirementProps {
  field: InstallerField;
  selectedIntegrations: IntegrationBase[];
  onSelectionChange: (integrations: IntegrationBase[]) => void;
  className?: string;
}

export const IntegrationMultiRequirement: React.FC<IntegrationMultiRequirementProps> = ({
  field,
  selectedIntegrations,
  onSelectionChange,
  className,
}) => {
  const { i18n } = useTranslation();
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  const maxCount = field.maxCount;
  const canAddMore = !maxCount || selectedIntegrations.length < maxCount;

  const getInitials = (name: string) =>
    name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  const handleRemove = (id: string) => {
    onSelectionChange(selectedIntegrations.filter((i) => i.id !== id));
  };

  const handleSelect = (integration: IntegrationBase) => {
    if (!selectedIntegrations.find((i) => i.id === integration.id)) {
      onSelectionChange([...selectedIntegrations, integration]);
    }
    setIsFlyoutOpen(false);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Selected integrations list */}
      {selectedIntegrations.length > 0 && (
        <div className="space-y-2">
          {selectedIntegrations.map((integration) => (
            <div
              key={integration.id}
              className="group flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 rounded-lg border border-border/50 transition-all duration-200"
            >
              {/* Avatar */}
              {integration.logo_url ? (
                <img
                  src={integration.logo_url}
                  alt={integration.name}
                  className="h-9 w-9 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-primary">
                    {getInitials(integration.name)}
                  </span>
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {integration.label || integration.name}
                </p>
                {getIntegrationDescription(integration, i18n.language) && (
                  <p className="text-xs text-muted-foreground truncate">
                    {getIntegrationDescription(integration, i18n.language)}
                  </p>
                )}
              </div>

              {/* Remove */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(integration.id)}
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all duration-200 flex-shrink-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add button — shown when no limit reached */}
      {canAddMore && (
        <button
          onClick={() => setIsFlyoutOpen(true)}
          disabled={isFlyoutOpen}
          className={cn(
            'w-full flex items-center justify-between px-4 bg-muted/20 rounded-lg border-2 border-dashed border-muted-foreground/20',
            'hover:border-primary hover:bg-primary/5 hover:text-primary transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isFlyoutOpen && 'border-primary bg-primary/5 text-primary',
            selectedIntegrations.length === 0 ? 'h-16' : 'h-11',
          )}
        >
          <div className="flex items-center gap-2">
            <Plug className="h-4 w-4" />
            <span className="text-sm font-medium">
              {selectedIntegrations.length === 0
                ? (field.label || field.name)
                : `Add ${field.label || field.name}`}
            </span>
          </div>
          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
            {isFlyoutOpen ? (
              <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus className="h-4 w-4 text-primary" />
            )}
          </div>
        </button>
      )}

      <IntegrationBaseSelector
        open={isFlyoutOpen}
        onClose={() => setIsFlyoutOpen(false)}
        onSelect={handleSelect}
        selectedIntegrations={selectedIntegrations.map((i) => i.id)}
        filters={field.filters}
        title={`Select ${field.label || field.name}`}
        searchPlaceholder={`Search ${(field.label || field.name).toLowerCase()}...`}
      />
    </div>
  );
};
